import { createFileRoute } from '@tanstack/react-router';
import { ArrowRight, Brush, ExternalLink, Layers, MessageSquare, Truck } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

export const Route = createFileRoute('/brands/avyraatlas')({
  component: AvyraAtlasCaseStudy,
});

const SNAPSHOT_METRICS = [
  { label: 'Vertical', value: 'Print-on-Demand lifestyle' },
  { label: 'Storefront', value: 'In-house (custom code)' },
  { label: 'Marketplaces', value: 'Etsy · Amazon · eBay' },
  { label: 'Fulfillment', value: 'Printify · Printful · Gelato' },
  { label: 'CX response target', value: 'Under 60 seconds' },
  { label: 'Operated by', value: 'AITOS Labs LLC' },
];

const WHAT_AITOS_DOES = [
  {
    icon: <Brush size={20} className="text-pink-400" />,
    title: 'In-house storefront',
    text: 'Avyra Atlas runs on the AITOS in-house storefront stack — the same custom-built codebase that powers Elzary.',
  },
  {
    icon: <Brush size={20} className="text-pink-400" />,
    title: 'Design pipeline',
    text: 'AI-assisted design generation, brand-consistent variants, and mockup automation tuned to the Avyra Atlas brand voice.',
  },
  {
    icon: <Layers size={20} className="text-pink-400" />,
    title: 'SEO listing engine',
    text: 'Multi-marketplace listing copy and tags (Etsy, Amazon, eBay), plus daily price/inventory sync.',
  },
  {
    icon: <Truck size={20} className="text-pink-400" />,
    title: 'Fulfillment routing',
    text: 'Per-SKU and per-region routing across Printify, Printful, and Gelato — with tracking sync and exception triage.',
  },
  {
    icon: <MessageSquare size={20} className="text-pink-400" />,
    title: 'CX & reviews ops',
    text: 'Sub-60s first response with LLM-drafted replies reviewed by humans. Reviews triage built in.',
  },
];

function AvyraAtlasCaseStudy() {
  return (
    <>
      <Helmet>
        <title>Avyra Atlas — Operated by AITOS Labs LLC</title>
        <meta
          name="description"
          content="Avyra Atlas is a Print-on-Demand lifestyle brand operated end-to-end by AITOS Labs LLC. Storefront, design, listing, fulfillment, CX, and growth on the AITOS ops stack."
        />
        <link rel="canonical" href="https://aitos.app/brands/avyraatlas" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Brand',
            name: 'Avyra Atlas',
            url: 'https://avyraatlas.com',
            description: 'Print-on-Demand lifestyle brand operated end-to-end by AITOS Labs LLC.',
            parentOrganization: {
              '@type': 'Organization',
              name: 'AITOS Labs LLC',
              url: 'https://aitos.app',
            },
          })}
        </script>
      </Helmet>

      <section className="pt-32 pb-12 px-6">
        <div className="max-w-5xl mx-auto">
          <p className="text-pink-400 text-xs tracking-widest mb-4">CASE STUDY · AVYRA ATLAS</p>
          <h1 className="text-5xl md:text-6xl font-bold mb-4">
            Avyra Atlas — operated by{' '}
            <span className="bg-gradient-to-r from-pink-400 to-amber-400 bg-clip-text text-transparent">
              AITOS
            </span>
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl mb-6">
            A Print-on-Demand lifestyle brand built on the AITOS in-house storefront and ops stack.
          </p>
          <a
            href="https://avyraatlas.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-colors"
          >
            Visit avyraatlas.com
            <ExternalLink size={16} />
          </a>
        </div>
      </section>

      <section className="py-12 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold mb-6">Snapshot</h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {SNAPSHOT_METRICS.map((m) => (
              <div key={m.label} className="bg-gray-900 rounded-2xl p-6 border border-gray-800">
                <p className="text-xs text-gray-500 uppercase tracking-widest mb-2">{m.label}</p>
                <p className="text-2xl font-bold text-white">{m.value}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-4">
            Detailed operating numbers (GMV, SKU count, repeat rate) shared on request.
          </p>
        </div>
      </section>

      <section className="py-12 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold mb-6">What AITOS does for Avyra Atlas</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {WHAT_AITOS_DOES.map((item) => (
              <div
                key={item.title}
                className="bg-gray-900 rounded-2xl p-6 border border-gray-800 hover:border-pink-500/30 transition-colors"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-pink-500/10 flex items-center justify-center">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-bold">{item.title}</h3>
                </div>
                <p className="text-gray-300 text-sm">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto bg-gradient-to-br from-pink-900/30 to-amber-900/20 rounded-3xl p-10 md:p-14 border border-pink-500/30 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-3">
            Want AITOS to operate your brand like this?
          </h2>
          <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
            Start with a 2-week brand audit. We diagnose, then design an operating agreement.
          </p>
          <a
            href="/#cta?intent=audit"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-pink-500 to-amber-500 text-white font-medium hover:opacity-90 transition-opacity"
          >
            Book a brand audit
            <ArrowRight size={18} />
          </a>
        </div>
      </section>
    </>
  );
}
